<script setup lang="ts">
/**
 * Botão base reutilizável com suporte a variantes, tamanhos e ícones.
 *
 * @example
 * ```vue
 * <!-- Primário grande com ícone -->
 * <BaseButton variant="primary" size="lg" @click="save">
 *   <v-icon name="md-save-outlined" /> Salvar
 * </BaseButton>
 *
 * <!-- Secundário pequeno -->
 * <BaseButton variant="secondary" size="sm" @click="edit">
 *   <v-icon name="md-edit-outlined" /> Editar
 * </BaseButton>
 *
 * <!-- Ghost full-width -->
 * <BaseButton variant="ghost" block @click="logout">
 *   <v-icon name="md-logout" /> Sair
 * </BaseButton>
 * ```
 */
withDefaults(
  defineProps<{
    variant?: "primary" | "secondary" | "ghost";
    size?: "sm" | "md" | "lg";
    block?: boolean;
    disabled?: boolean;
  }>(),
  {
    variant: "primary",
    size: "md",
    block: false,
    disabled: false,
  }
);
</script>

<template>
  <button
    class="base-btn"
    :class="[
      `base-btn--${variant}`,
      `base-btn--${size}`,
      { 'base-btn--block': block }
    ]"
    :disabled="disabled"
    v-bind="$attrs"
  >
    <slot />
  </button>
</template>


<style lang="scss" scoped>
.base-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--font-size-md);
  font-family: var(--font-family);
  font-weight: 500;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: all var(--transition);
  border: 1px solid transparent;
  min-height: 36px;
  line-height: 1.4;
}

.base-btn .ov-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.base-btn--primary {
  background-color: var(--colorPrimary);
  color: var(--colorTextInverted);
  border-color: var(--colorPrimary);
}
.base-btn--primary:hover:not(:disabled) {
  background-color: var(--colorPrimaryHover);
  border-color: var(--colorPrimaryHover);
}
.base-btn--primary:active:not(:disabled) {
  background-color: var(--colorPrimaryPressed);
  border-color: var(--colorPrimaryPressed);
}
.base-btn--primary:focus-visible {
  outline: 2px solid var(--colorBorderFocus);
  outline-offset: 2px;
}

.base-btn--secondary {
  background-color: transparent;
  color: var(--colorText);
  border-color: var(--colorBorder);
}
.base-btn--secondary:hover:not(:disabled) {
  background-color: var(--colorBackgroundHover);
}
.base-btn--secondary:active:not(:disabled) {
  background-color: var(--colorBackgroundPressed);
}
.base-btn--secondary:focus-visible {
  outline: 2px solid var(--colorBorderFocus);
  outline-offset: 2px;
}

.base-btn--ghost {
  background-color: transparent;
  color: var(--colorTextSecondary);
  border-color: transparent;
}
.base-btn--ghost:hover:not(:disabled) {
  background-color: var(--colorBackgroundHover);
  color: var(--colorText);
}
.base-btn--ghost:active:not(:disabled) {
  background-color: var(--colorBackgroundPressed);
}
.base-btn--ghost:focus-visible {
  outline: 2px solid var(--colorBorderFocus);
  outline-offset: 2px;
}

.base-btn--sm {
  padding: var(--spacing-xs) var(--spacing-sm);
  font-size: var(--font-size-sm);
  min-height: 28px;
}

.base-btn--lg {
  padding: 12px var(--spacing-lg);
  font-size: var(--font-size-lg);
  min-height: 44px;
}

.base-btn--block {
  width: 100%;
}

.base-btn:disabled {
  color: var(--colorTextDisabled);
  background-color: var(--colorBackgroundDisabled);
  border-color: var(--colorBorderDisabled);
  cursor: not-allowed;
}
</style>
